import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RoleAwareLanding } from './guards';
import { AppLayout } from '@/layouts/AppLayout';
import { LoginPage } from '@/pages/login/LoginPage';
import { CompliancePage } from '@/pages/compliance/CompliancePage';
import { CasesPage } from '@/pages/cases/CasesPage';
import { NotFound } from '@/pages/NotFound';
import { LoadingSkeleton } from '@/components/states/LoadingSkeleton';
import { AuthGuard, RoleGuard } from './guards';

// M3-ρ-5：监控/对比看板 — lazy 分包（含 SVG 折线 + 4 Tab 组件树）
const MonitoringPage = lazy(() =>
  import('@/pages/monitoring/MonitoringPage').then((m) => ({
    default: m.MonitoringPage,
  })),
);

// R3-Sprint5:三角色子门户(由 Implementer 落地 — Architect 写路由占位)
const EnterprisePortal = lazy(() =>
  import('@/pages/me/EnterprisePortal').then((m) => ({ default: m.EnterprisePortal })),
);
const MentorPortal = lazy(() =>
  import('@/pages/me/MentorPortal').then((m) => ({ default: m.MentorPortal })),
);
const InvestorPortal = lazy(() =>
  import('@/pages/me/InvestorPortal').then((m) => ({ default: m.InvestorPortal })),
);

// R3-Sprint6 Sec L4 follow-up:无主企业 admin 重绑(由 Implementer 落地 — Architect 写路由占位)
const OrphanEnterprisesPage = lazy(() => import('@/pages/admin/OrphanEnterprisesPage'));

// R3-Sprint7a:5 页补缺(Architect 写路由 + stub — Implementer Group A/B 落地业务)
const SubjectsPage = lazy(() => import('@/pages/subjects/SubjectsPage'));
// R3-Sprint12-A FR-01:主体详情(4 Tab — 基本信息/申报案/资料/服务+投融资)
const SubjectDetailPage = lazy(() => import('@/pages/subjects/SubjectDetailPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
// Sprint 20-extra: ReadinessPage 已并入 CompliancePage,/readiness 路由改 Navigate 重定向,
// 源文件 frontend/src/pages/readiness/ReadinessPage.tsx 暂保留(自有 unit test 直接 mount,不依赖路由)。
const AuditLogsPage = lazy(() => import('@/pages/audit-logs/AuditLogsPage'));
const PolicyPacksPage = lazy(() => import('@/pages/policy-packs/PolicyPacksPage'));

// R3-Sprint7b:3 页补缺(Architect 写路由 + stub — Implementer Group C/D 落地业务)
//   /materials       —— 独立材料台账(原嵌 cases 内,Sprint 7b 抽出 admin 视图)
// Sprint41:/cockpit 已废 — 真业务由 Sprint 32 「我的待办」覆盖且更精(4 优先级 + 6 子源)
//   路由改 Navigate 重定向到 /my-todos;30 天后可删本兜底
//   /tenants/theme   —— 白标主题配置(branding CRUD + logo + 色彩 picker + 实时预览)
const MaterialsPage = lazy(() => import('@/pages/materials/MaterialsPage'));
// Sprint41:CockpitPage 已删 — 整页 624 行 + 2 测试 + api/cockpit.ts 一起砍
const TenantThemePage = lazy(() => import('@/pages/tenants/TenantThemePage'));
// Sprint 20-extra:企业资质申报看板(全租户管理者视角 — 表格 + 进度 + 导出)
const DeclarationDashboardPage = lazy(
  () => import('@/pages/declaration-dashboard/DeclarationDashboardPage'),
);

// R3-Sprint10:用户/租户生命周期 UI(v5 §13)
//   /_admin/tenants  —— 租户管理(platform_operator 专属)
//   /_admin/users    —— 用户管理(platform_operator + tenant_admin)
//   /accept/:token   —— 公开邀请激活页(无 layout)
const TenantsAdminPage = lazy(() => import('@/pages/_admin/TenantsPage'));
const UsersAdminPage = lazy(() => import('@/pages/_admin/UsersAdminPage'));
const AcceptInvitePage = lazy(() => import('@/pages/accept/AcceptInvitePage'));

// R3-Sprint13 Phase 2:LLM 设置页
// Sprint36:PolicyPackChecklistPage 已删 — 内容并入主页指标 Tab(序号/材料/原文出处/必填→必备)
const LLMSettingsPage = lazy(() => import('@/pages/_admin/LLMSettingsPage'));
// Sprint 16:资质目录(platform_operator 专属)
const QualificationsAdminPage = lazy(() => import('@/pages/_admin/QualificationsAdminPage'));

// Sprint 20 S20-B:孵化器档案录入(tenant_admin / platform_operator)
// — prod subjects[subject_type='incubator'] 当前 0 行,T1-T4 全红;本页为录入入口
const IncubatorProfilePage = lazy(
  () => import('@/pages/incubator/IncubatorProfilePage'),
);

// Sprint 20 S20-C1:申报案工作台(/cases/:id) — 指标红绿灯 + 时间轴 + 案信息 sticky 顶
const CaseWorkbenchPage = lazy(() =>
  import('@/pages/cases/CaseWorkbenchPage').then((m) => ({
    default: m.CaseWorkbenchPage,
  })),
);

// Sprint 32-C: 我的待办(企服 + 管理者落地页)
const MyTodosPage = lazy(() => import('@/pages/my-todos/MyTodosPage'));

// Sprint 52 Phase 1C:应收账单看板(独立 route /receivables)
const ReceivablesDashboard = lazy(
  () => import('@/pages/receivables/ReceivablesDashboard'),
);

// Sprint57-C / Phase C:报表中心 + 单报表详情(53 字段半年报)
const ReportsCenterPage = lazy(() => import('@/pages/reports/ReportsCenterPage'));
const ReportDetailPage = lazy(() => import('@/pages/reports/ReportDetailPage'));

// Sprint57-D / Phase D:任务系统 + JXY 驾驶舱
//   /tasks/my-todos   — WY 今日任务(任务系统视角,与老 /my-todos 共存)
//   /tasks            — JXY 任务中心(管理者派工 + 全租户列表)
//   /dashboard/jxy    — JXY 管理者驾驶舱(健康度 + 申报准备度 + 任务汇总)
const TasksMyTodosPage = lazy(() => import('@/pages/tasks/MyTodosPage'));
const TasksListPage = lazy(() => import('@/pages/tasks/TasksListPage'));
const JxyDashboardPage = lazy(() => import('@/pages/dashboard/JxyDashboardPage'));

/**
 * 路由表 — M3-π 仅 Login + Subjects DEMO + 404。
 * 后续 ρ-1 ~ ρ-15 业务页在此追加。
 */
export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      ),
      children: [
        // Sprint47-fix:根路由按角色感知跳 — enterprise → /me/enterprise,
        // staff/manager/tenant_admin → /my-todos,其余 → /dashboard。
        // 修前写死 /dashboard,导致企业主从主域名直接进显 403。
        { index: true, element: <RoleAwareLanding /> },
        {
          // R3-Sprint7a:/subjects 切真接口(原 SubjectsDemo mock 文件保留但不再路由)
          path: 'subjects',
          element: (
            <RoleGuard menuKey="/subjects">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <SubjectsPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          // R3-Sprint12-A FR-01:主体详情页(4 Tab)— 复用 /subjects menuKey 权限
          path: 'subjects/:id',
          element: (
            <RoleGuard menuKey="/subjects">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <SubjectDetailPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          // R3-Sprint7a:平台/租户/健康度概览(原菜单 broken link,Sprint 7a 补)
          path: 'dashboard',
          element: (
            <RoleGuard menuKey="/dashboard">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <DashboardPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // S2 退 R1(2026-07-01):企业台账 /ledger 路由 + pages/ledger + api/ledger.ts 已删
        // (Sprint31-I 淘汰,观察期已过;后端 /periods/{id}/ledger 端点同批退役)。
        {
          // M3-ρ-2：达标看板（第二个真接口业务页 — 只读）
          path: 'compliance',
          element: (
            <RoleGuard menuKey="/compliance">
              <CompliancePage />
            </RoleGuard>
          ),
        },
        {
          // M3-ρ-3：申报案 Kanban（第三个真接口业务页 — 7 态状态机）
          path: 'cases',
          element: (
            <RoleGuard menuKey="/cases">
              <CasesPage />
            </RoleGuard>
          ),
        },
        {
          // Sprint 20 S20-C1:申报案工作台详情(指标 / 材料 / 快照 / 时间轴)
          path: 'cases/:id',
          element: (
            <RoleGuard menuKey="/cases">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <CaseWorkbenchPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          // Sprint 20-extra:企业资质申报看板(管理者鸟瞰 — 进行中案表格 + 进度条 + 导出)
          path: 'declaration-dashboard',
          element: (
            <RoleGuard menuKey="/declaration-dashboard">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <DeclarationDashboardPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          // M3-ρ-5：监控/对比看板 — 4 Tab 接 M3-η 7 endpoint
          path: 'monitoring',
          element: (
            <RoleGuard menuKey="/monitoring">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <MonitoringPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint5:三角色子门户 — /me/{enterprise,mentor,investor}
        // 角色 guard 用 allow= 列表(不走 menuKey 因为 MENU_VISIBILITY 矩阵尚未含此 3 key)
        {
          path: 'me/enterprise',
          element: (
            <RoleGuard allow={['enterprise', 'tenant_admin', 'platform_operator']}>
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <EnterprisePortal />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          path: 'me/mentor',
          element: (
            <RoleGuard allow={['mentor', 'tenant_admin', 'platform_operator']}>
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <MentorPortal />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          path: 'me/investor',
          element: (
            <RoleGuard allow={['investor', 'tenant_admin', 'platform_operator']}>
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <InvestorPortal />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint7a:就绪检查报告 + PDF 下载
        // Sprint 20-extra:已合并到 /compliance(孵化器达标看板)— 老书签 301 等价重定向,防 404
        {
          path: 'readiness',
          element: <Navigate to="/compliance" replace />,
        },
        // R3-Sprint7a:审计日志查询(17 endpoint × 26 action)
        {
          path: 'audit-logs',
          element: (
            <RoleGuard menuKey="/audit-logs">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <AuditLogsPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint7a:政策包 CRUD + 阈值矩阵编辑(6 态 threshold_json)
        {
          path: 'policy-packs',
          element: (
            <RoleGuard menuKey="/policy-packs">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <PolicyPacksPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint36:政策包汇总页 /policy-packs/:id/checklist 已删 — 内容并入主页指标 Tab
        // R3-Sprint7b:独立材料台账(跨 case 历史归档 + admin 浏览)
        {
          path: 'materials',
          element: (
            <RoleGuard menuKey="/materials">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <MaterialsPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint 52 Phase 1C:应收账单看板(平台运营 / 租户管理员 / 企服)
        {
          path: 'receivables',
          element: (
            <RoleGuard menuKey="/receivables">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <ReceivablesDashboard />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint 32-C: 我的待办(企服/管理者落地页)
        {
          path: 'my-todos',
          element: (
            <RoleGuard menuKey="/my-todos">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <MyTodosPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint41:/cockpit 已废 — 真业务由「我的待办」覆盖;30 天兜底跳转
        {
          path: 'cockpit',
          element: <Navigate to="/my-todos" replace />,
        },
        // R3-Sprint7b:白标主题配置(branding CRUD + 色彩 picker + 实时预览)
        // 注:path 含 `/`,react-router v6 接受字面量(不用嵌套 children)
        {
          path: 'tenants/theme',
          element: (
            <RoleGuard menuKey="/tenants/theme">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <TenantThemePage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint6 Sec L4 follow-up:无主企业 admin 重绑页
        // RBAC:platform_operator(跨租户运营)+ tenant_admin(本租户管理员)
        // 注:本仓库 Role 枚举无 `admin`,以 `platform_operator` / `tenant_admin` 代之
        {
          path: 'admin/orphan-enterprises',
          element: (
            <RoleGuard allow={['platform_operator', 'tenant_admin']}>
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <OrphanEnterprisesPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint10:租户管理(platform_operator 专属)
        {
          path: '_admin/tenants',
          element: (
            <RoleGuard menuKey="/_admin/tenants">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <TenantsAdminPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint10:用户管理(platform_operator + tenant_admin)
        {
          path: '_admin/users',
          element: (
            <RoleGuard menuKey="/_admin/users">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <UsersAdminPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // R3-Sprint13 Phase 2:LLM 提供方配置 + 调用历史(platform_operator 专属)
        {
          path: '_admin/llm-settings',
          element: (
            <RoleGuard menuKey="/_admin/llm-settings">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <LLMSettingsPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint 16:资质目录(platform_operator 专属)
        {
          path: '_admin/qualifications',
          element: (
            <RoleGuard menuKey="/_admin/qualifications">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <QualificationsAdminPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint57-C / Phase C:报表中心(政府数据交付中枢) + 半年报详情(53 字段)
        {
          path: 'reports',
          element: (
            <RoleGuard menuKey="/reports">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <ReportsCenterPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          path: 'reports/:code',
          element: (
            <RoleGuard menuKey="/reports">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <ReportDetailPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint57-D / Phase D:任务系统(WY 今日任务 + JXY 任务中心)
        {
          path: 'tasks/my-todos',
          element: (
            <RoleGuard menuKey="/tasks/my-todos">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <TasksMyTodosPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        {
          path: 'tasks',
          element: (
            <RoleGuard menuKey="/tasks">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <TasksListPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint57-D / Phase D:JXY 管理者驾驶舱(健康度 + 申报准备度 + 任务汇总)
        {
          path: 'dashboard/jxy',
          element: (
            <RoleGuard menuKey="/dashboard/jxy">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <JxyDashboardPage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // Sprint 20 S20-B:孵化器档案录入(tenant_admin / platform_operator)
        {
          path: 'incubator/profile',
          element: (
            <RoleGuard menuKey="/incubator/profile">
              <Suspense fallback={<LoadingSkeleton rows={6} />}>
                <IncubatorProfilePage />
              </Suspense>
            </RoleGuard>
          ),
        },
        // 后续 ρ-4 ~ ρ-15 在此追加
      ],
    },
    // R3-Sprint10:公开邀请激活页(无 layout,无 JWT)— 与 /login 平级,不进 AppLayout
    {
      path: '/accept/:token',
      element: (
        <Suspense fallback={<LoadingSkeleton rows={6} />}>
          <AcceptInvitePage />
        </Suspense>
      ),
    },
    { path: '*', element: <NotFound /> },
  ],
  {
    // M3-π-QA Reviewer high#4：提前 opt-in v7 future flags，消除 console warn + 减少 v7 升级摩擦
    // 注：v7_startTransition 是 RouterProvider 的 prop（不在 createBrowserRouter），留 M3-ρ Architect 范围
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
